import { HttpClient, HttpContext } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, of, retry, throwError } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { environment } from "src/environments/environment";
import {
    CompleteRegistrationRequest,
    GenerateOtpRequestForRegistration, JiraVerifyUserRequest,
    RegistrationRequest,
} from "../models/registration-request";
import { SharedService } from "./shared.service";
import { APIResponse } from "../models/Response";
import { UserProfileOptionsResponse } from "../modules/shared/Models/registration.model";
import { UserDetailsResponse } from "../models/user";
import { IS_PRIVATE_API } from "./auth.interceptor";
import { LoginResponse } from "../modules/shared/Models/login-model.model";
import { LoginService } from "./login.service";
import { UserProfileService } from "../modules/user/services/user-profile.service";

@Injectable({
    providedIn: "root",
})
export class RegistrationService {
    apiServerUrl = environment.apiBaseUrl;

    constructor(
        private http: HttpClient,
        private sharedService: SharedService,
        private loginService: LoginService,
        private userProfileService: UserProfileService,
    ) {}

    generateOtp(request: RegistrationRequest): Observable<any> {
        return this.http.post(`${this.apiServerUrl}/auth/generateotp`, request).pipe(
            catchError((error) => {
                return throwError(error);
            }),
        );
    }

    generateOtpForRegistration(request: GenerateOtpRequestForRegistration) {
        return this.http.post<APIResponse<string>>(`${this.apiServerUrl}/auth/generateotp`, request).pipe(
            catchError((error) => {
                return throwError(error);
            }),
        );
    }

    signUp(request: RegistrationRequest): Observable<any> {
        return this.http.post(`${this.apiServerUrl}/auth/signup`, request).pipe(
            catchError((error) => {
                return throwError(error);
            }),
        );
    }

    signUpWithOtp(request: GenerateOtpRequestForRegistration): Observable<APIResponse<LoginResponse>> {
        return this.http.post<APIResponse<LoginResponse>>(`${this.apiServerUrl}/auth/signup`, request).pipe(
            catchError((error) => {
                return throwError(error);
            }),
        );
    }

    completeRegistration(request: CompleteRegistrationRequest): Observable<any> {
        return this.http.post(`${this.apiServerUrl}/auth/completeRegistration`, request).pipe(
            catchError((error) => {
                return throwError(error);
            }),
        );
    }

    fetchAndUpdateUserDetailsOnLogin(userName: string) {
        return this.http.get(`${this.apiServerUrl}/user/getUserDetails/${userName}`).pipe(
            catchError((err) => {
                return throwError(err);
            }),
        );
    }

    fetchAndUpdateUserDetails(userName: string): Observable<boolean> {
        return this.http
            .get<APIResponse<UserDetailsResponse>>(`${this.apiServerUrl}/user/getUserDetails/${userName}`)
            .pipe(
                retry(2), // Retry up to 2 times in case of failure
                map((response) => {
                    this.sharedService.storeAccessDomains(response.data.user.accessDomains);
                    this.sharedService.setUserAccounts(response.data.user.userAccount);
                    this.sharedService.storeUserCountryDetails(response.data.user.fkCountryId);
                    this.userProfileService.setProfilePicture(response.data.user.imageData);
                    const { firstName, lastName, userId, createdDateTime } = response.data.user;
                    this.loginService.setUserDetailsOnLogin(
                        firstName,
                        lastName,
                        userId,
                        response.data.user.primaryEmail,
                        createdDateTime,
                    );
                    this.checkIsTokenIdsAndUserAccountsSame();
                    return true; // Successful operations
                }),
                catchError((error: any) => {
                    console.log(error);
                    return of(false); // Error occurred
                }),
            );
    }

    getAllOptionsForRegistration(): Observable<APIResponse<UserProfileOptionsResponse>> {
        return this.http
            .get<APIResponse<UserProfileOptionsResponse>>(`${this.apiServerUrl}/register/getAllOptionsForRegistration`)
            .pipe(
                catchError((error) => {
                    return throwError(error);
                }),
            );
    }

    isOrgNameExists(orgName: string): Observable<any> {
        let newRequestParams = "?orgName=" + orgName;
        return this.http
            .get(`${this.apiServerUrl}/organization/exists${newRequestParams}`, {
                context: new HttpContext().set(IS_PRIVATE_API, false),
            })
            .pipe(
                catchError((error) => {
                    return throwError(error);
                }),
            );
    }

    private checkIsTokenIdsAndUserAccountsSame() {
        let userAccountIds = this.sharedService.getAllAccountIdsOfLoggedInUser(false) as number[];
        let tokenAccountIds = this.sharedService.getUserAccountIdsFromToken();
        let validIds = this.sharedService.areListsIdentical(userAccountIds, tokenAccountIds);
        if (!validIds) {
            this.loginService.logoutTheUserAndRedirectToLogin();
        }
    }

    verifyJiraUserAndSendOTP(request: JiraVerifyUserRequest){
        return this.http.post<APIResponse<string>>(
            `${this.apiServerUrl}/jira/verifyJiraUserAndSendOtp`,
            request,
        );
    }
    verifyOTPAndActivateUser(request: JiraVerifyUserRequest): Observable<APIResponse<string>>{
        return this.http
            .post<APIResponse<string>>(`${this.apiServerUrl}/jira/verifyOtpAndActivateUser`,request)
            .pipe(
                catchError((error) => {
                    return throwError(error);
                }),
            );
    }
}
